import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { NewsPost } from '../../data/newsPosts'
import { useLocale } from '../../i18n/locale'
import { useT } from '../../i18n/t'

type NewsArticleCardProps = {
  post: NewsPost
  /** Pełna ścieżka artykułu, np. /o-projekcie/aktualnosci/slug */
  articlePath: string
}

function formatNewsDate(iso: string, localeTag: string): string {
  const d = new Date(`${iso}T12:00:00`)
  return d.toLocaleDateString(localeTag, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

export function NewsArticleCard({ post, articlePath }: NewsArticleCardProps) {
  const { t } = useT()
  const { localeTag } = useLocale()

  return (
    <article className="flex h-full flex-col">
      <Link
        to={articlePath}
        className="group block overflow-hidden rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
      >
        <div className="relative aspect-[4/3] overflow-hidden rounded-lg">
          <img
            src={post.coverImage}
            alt=""
            loading="lazy"
            className={
              post.coverFit === 'contain'
                ? 'h-full w-full bg-paper object-contain p-6 transition duration-500 group-hover:scale-[1.03]'
                : 'h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]'
            }
          />
        </div>
      </Link>
      <div className="mt-4 flex flex-1 flex-col">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-ink/45">
          {t(post.categoryKey)}
          <span className="mx-2 font-normal opacity-40" aria-hidden>
            ·
          </span>
          <time dateTime={post.publishedAt}>{formatNewsDate(post.publishedAt, localeTag)}</time>
        </p>
        <h3 className="mt-2 text-lg font-extrabold leading-snug text-ink md:text-xl">
          <Link
            to={articlePath}
            className="rounded-sm decoration-ink/20 underline-offset-4 transition hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
          >
            {t(post.titleKey)}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-3 flex-1 text-sm font-medium leading-relaxed text-ink/65">
          {t(post.excerptKey)}
        </p>
        <Link
          to={articlePath}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-extrabold text-ink transition hover:text-ink/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
        >
          {t('news.card.readMore')}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </article>
  )
}
